import { useMemo } from 'react';
import { parse, format } from 'date-fns';
import { checkPackageAvailability, isEarlyAccessAvailable } from '../util/package';

const LIMITED_OFFERING_THRESHOLD = 5;

const getLimitedOfferingLabel = (activity, count) => {
  if (activity === 'hunt') {
    return count === 1 ? 'Hunt' : 'Hunts';
  }

  return count === 1 ? 'Trip' : 'Trips';
};

/**
 * Custom hook to calculate package availability badges
 * @param {Object} packageItem - The package object
 * @param {Object} intl - Intl object for formatting
 * @returns {Object} - Availability badges plus optional supporting text
 */
const usePackageAvailability = (packageItem, intl) => {
  return useMemo(() => {
    if (!packageItem) {
      return {
        availabilityBadges: [],
      };
    }

    const availabilityBadges = [];
    let supportingText;

    // Get availability data
    const { bookingRangeIsFuture, bookingRangeIsPast } = checkPackageAvailability(packageItem);

    const {
      quantity,
      availableDateFrom,
      hasQuantityRestricted,
      earlyAccessExpiration,
      activity,
    } = packageItem;

    const quantityBooked = quantity?.booked ? quantity.booked : 0;
    const quantityTotal = quantity?.total || 0;
    const remainingQuantity = quantityTotal - quantityBooked;

    // Check if package should show available overlay
    const isAvailable = quantityBooked < quantityTotal;
    const showPackageAvailableOverlay =
      (hasQuantityRestricted && !isAvailable) || bookingRangeIsFuture || bookingRangeIsPast;

    const showEarlyAccessBadge =
      isEarlyAccessAvailable(earlyAccessExpiration) && !showPackageAvailableOverlay;

    // Handle date-based logic first (top-left badge)
    if (bookingRangeIsPast) {
      availabilityBadges.push({
        text: intl.formatMessage({ id: 'PackageCard.soldOut', defaultMessage: 'Sold Out' }),
        variant: 'error',
        position: 'top-left',
        showOverlay: true,
      });
    } else if (availableDateFrom && bookingRangeIsFuture) {
      const date = parse(availableDateFrom);
      const formattedDate = format(date, 'MMM. D');
      availabilityBadges.push({
        text: intl.formatMessage(
          {
            id: 'PackageCard.bookInFuture',
            defaultMessage: 'Accepting Bookings {date}',
          },
          {
            date: formattedDate,
          }
        ),
        variant: 'warning',
        position: 'top-left',
        showOverlay: true,
      });
    }
    // Apply quantity-based logic (top-left badge)
    else if (hasQuantityRestricted && quantityTotal > 0 && quantityBooked >= quantityTotal) {
      // Rule 5: When quantity <= booked (fully booked or overbooked)
      availabilityBadges.push({
        text: intl.formatMessage({ id: 'PackageCard.soldOut', defaultMessage: 'Sold Out' }),
        variant: 'error',
        position: 'top-left',
        showOverlay: true,
      });
    } else if (hasQuantityRestricted && quantityBooked >= 1 && quantityBooked < quantityTotal) {
      // Show booking progress for any partially booked restricted package.
      availabilityBadges.push({
        text: intl.formatMessage(
          {
            id: 'PackageCard.bookedProgress',
            defaultMessage: '{booked} of {total} Booked',
          },
          {
            booked: quantityBooked,
            total: quantityTotal,
          }
        ),
        variant: 'booked',
        position: 'top-left',
      });
      if (activity === 'hunt' && remainingQuantity === 1) {
        supportingText = intl.formatMessage({
          id: 'PackageCard.remainingOneHunt',
          defaultMessage: '1 hunt remaining this season',
        });
      }
    } else if (
      hasQuantityRestricted &&
      quantityTotal >= 1 &&
      quantityTotal <= LIMITED_OFFERING_THRESHOLD &&
      quantityBooked === 0
    ) {
      // Small restricted inventories highlight the total offered for the season.
      availabilityBadges.push({
        text: intl.formatMessage(
          {
            id: 'PackageCard.limitedOffering',
            defaultMessage: 'Only {count} {label} Offered',
          },
          {
            count: quantityTotal,
            label: getLimitedOfferingLabel(activity, quantityTotal),
          }
        ),
        variant: 'limited',
        position: 'top-left',
      });
    } else {
      // Default availability state for unrestricted, large, or malformed quantities.
      availabilityBadges.push({
        text: intl.formatMessage({
          id: 'PackageCard.available',
          defaultMessage: 'Available',
        }),
        variant: 'warning',
        position: 'top-left',
      });
    }

    // Check for early access badge (bottom-right badge)
    if (showEarlyAccessBadge) {
      availabilityBadges.push({
        text: intl.formatMessage({
          id: 'PackageCard.earlyAccess',
          defaultMessage: 'Early Access',
        }),
        variant: 'action',
        position: 'bottom-right',
      });
    }

    return {
      availabilityBadges,
      supportingText,
    };
  }, [packageItem, intl]);
};

export default usePackageAvailability;
